import { formatClinicalCalendarDay, formatClinicalDateTime } from '../i18n/localeFormat';
import { getVitalFlagSeverity, getVitalCellColorClass, getMedicationFlagState } from './clinicalFlags';

const VITAL_COLUMNS = ['bp', 'pulse', 'temp', 'o2', 'rr', 'pain', 'bg'];

/** @deprecated Prefer formatFlowSheetDateTime — kept for callers that only need HH:MM fallback. */
function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Continuous 24h label: "15 Jul 08:00" (never shift-based). */
export function formatFlowSheetDateTime(isoOrDate) {
  if (!isoOrDate) return '—';
  const formatted = formatClinicalDateTime(isoOrDate, 'en');
  return formatted || '—';
}

function dash(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

export function getCalendarDayKey(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Resolve a full datetime for fluid / MAR / observation rows.
 * Prefer stored datetime; otherwise compose from HH:MM + reference day (note datetime or today).
 */
export function resolveEntryDateTime(entry, referenceIso = null) {
  if (!entry) return null;
  if (entry.datetime) {
    const parsed = new Date(entry.datetime);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  const timeText = String(entry.time || entry.time_given || '').trim();
  const hhmm = timeText.match(/^(\d{1,2}):(\d{2})$/);
  const base = referenceIso ? new Date(referenceIso) : new Date();
  if (Number.isNaN(base.getTime())) return null;

  if (hhmm) {
    const resolved = new Date(base);
    resolved.setHours(Number(hhmm[1]), Number(hhmm[2]), 0, 0);
    return resolved;
  }

  if (referenceIso) {
    const fromRef = new Date(referenceIso);
    if (!Number.isNaN(fromRef.getTime())) return fromRef;
  }

  return null;
}

export function composeDateTimeFromClockTime(hhmm, reference = new Date()) {
  const match = String(hhmm || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  const base = reference instanceof Date ? new Date(reference) : new Date(reference);
  if (!match || Number.isNaN(base.getTime())) return base.toISOString();
  base.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return base.toISOString();
}

export function filterNotesForPatient(sessionNotes, patientId, patientName) {
  const nameKey = patientName?.toLowerCase().trim();
  return sessionNotes
    .filter((note) => {
      if (note.patient_id && patientId) return note.patient_id === patientId;
      if (nameKey && note.patient_name) {
        return note.patient_name.toLowerCase().includes(nameKey.split(' ')[0]);
      }
      return false;
    })
    .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
}

function getNoteContent(note) {
  return note.openaiNote || note.note_content || {};
}

export function buildVitalsRows(sessionNotes, patientId, patientName) {
  const notes = filterNotesForPatient(sessionNotes, patientId, patientName).filter(
    (n) => n.note_type === 'vitals'
  );

  return notes.map((note) => {
    const v = getNoteContent(note).vitals || {};
    const bp =
      v.bp_systolic != null && v.bp_diastolic != null
        ? `${v.bp_systolic}/${v.bp_diastolic}`
        : null;

    return {
      id: note.id,
      time: formatFlowSheetDateTime(note.datetime),
      datetime: note.datetime,
      flags: getNoteContent(note).flags || [],
      bp,
      bpSystolic: v.bp_systolic,
      bpDiastolic: v.bp_diastolic,
      pulse: v.pulse,
      temp: v.temp_celsius,
      o2: v.o2_sat,
      rr: v.rr,
      pain: v.pain,
      bg: v.blood_sugar,
    };
  });
}

export function getVitalCellStatus(key, row) {
  const displayValue = formatVitalDisplay(row, key);
  const severity = getVitalFlagSeverity(row.flags || [], key, displayValue);
  if (severity === 'critical') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'normal';
}

export { getVitalCellColorClass };

function compareTrend(current, previous, key) {
  const cur = parseFloat(current);
  const prev = parseFloat(previous);
  if (Number.isNaN(cur) || Number.isNaN(prev) || current == null || previous == null) {
    return 'stable';
  }

  const higherIsWorse = ['pain', 'temp', 'pulse', 'rr', 'bg'].includes(key);
  const lowerIsWorse = ['o2'].includes(key);

  if (key === 'bp') {
    const curSys = parseFloat(String(current).split('/')[0]);
    const prevSys = parseFloat(String(previous).split('/')[0]);
    if (Number.isNaN(curSys) || Number.isNaN(prevSys)) return 'stable';
    if (curSys > prevSys + 5) return 'worsening';
    if (curSys < prevSys - 5) return 'improving';
    return 'stable';
  }

  const delta = cur - prev;
  const threshold = key === 'pain' ? 1 : key === 'temp' ? 0.3 : 3;

  if (Math.abs(delta) < threshold) return 'stable';
  if (higherIsWorse) return delta > 0 ? 'worsening' : 'improving';
  if (lowerIsWorse) return delta < 0 ? 'worsening' : 'improving';
  return delta > 0 ? 'improving' : 'worsening';
}

export function buildVitalTrends(rows) {
  if (rows.length < 2) return null;
  const last = rows[rows.length - 1];
  const prev = rows[rows.length - 2];
  const trends = {};

  VITAL_COLUMNS.forEach((key) => {
    trends[key] = compareTrend(last[key], prev[key], key);
  });

  return trends;
}

export function formatVitalDisplay(row, key) {
  if (key === 'bp') return dash(row.bp);
  if (key === 'temp' && row.temp != null) return `${row.temp}`;
  return dash(row[key]);
}

function classifyObservationText(text) {
  const lower = (text || '').toLowerCase();
  if (/physician|doctor|دكتور|notified|contacted|ordered/.test(lower)) return 'physician';
  if (/incident|deteriorat|fall|emergency|sepsis|unresponsive/.test(lower)) return 'incident';
  if (/oxygen|dressing|inserted|reposition|nebulizer|blood drawn|ecg|catheter|applied|intervention/.test(lower)) {
    return 'intervention';
  }
  return 'observation';
}

function splitIntoEntries(text) {
  if (!text?.trim()) return [];
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function sortByResolvedDateTime(a, b) {
  const aTime = a.sortAt ? new Date(a.sortAt).getTime() : 0;
  const bTime = b.sortAt ? new Date(b.sortAt).getTime() : 0;
  return aTime - bTime;
}

export function buildMedicationRows(sessionNotes, patientId, patientName, manualMeds = []) {
  const rows = [];
  const notes = filterNotesForPatient(sessionNotes, patientId, patientName).filter(
    (n) => n.note_type === 'medication_admin'
  );

  notes.forEach((note) => {
    const content = getNoteContent(note);
    const meds = content.medications_administered?.length
      ? content.medications_administered
      : content.medications || [];

    meds.forEach((med, index) => {
      const dose =
        med.dose_amount != null && med.dose_unit
          ? `${med.dose_amount}${med.dose_unit}`
          : med.dose || '—';
      const resolved = resolveEntryDateTime(
        { time: med.time_given, datetime: med.datetime || note.datetime },
        note.datetime
      );
      rows.push({
        id: `${note.id}-${index}`,
        time: formatFlowSheetDateTime(resolved || note.datetime),
        sortAt: (resolved || new Date(note.datetime)).toISOString(),
        medication: med.drug_name || med.name || '—',
        dose,
        route: med.route || '—',
        response: med.patient_response || med.response || '—',
        highAlert: getMedicationFlagState(content.flags || [], med.drug_name || med.name).highAlert,
        adverseReaction: Boolean(med.adverse_reaction),
        notGiven: Boolean(med.not_given),
        deviationReason: med.deviation_reason || null,
        deviationReasonId: med.deviation_reason_id || null,
        scheduledTime: med.scheduled_time || med.time_given || null,
        source: 'auto',
      });
    });
  });

  manualMeds.forEach((med) => {
    const resolved = resolveEntryDateTime(med, med.datetime || null);
    rows.push({
      id: med.id,
      time: formatFlowSheetDateTime(resolved) !== '—'
        ? formatFlowSheetDateTime(resolved)
        : med.time || '—',
      sortAt: resolved ? resolved.toISOString() : composeDateTimeFromClockTime(med.time),
      medication: med.drugName,
      dose: med.dose || '—',
      route: med.route || '—',
      response: med.response || '—',
      highAlert: false,
      adverseReaction: false,
      source: 'manual',
    });
  });

  return rows.sort(sortByResolvedDateTime);
}

export function buildObservationEntries(sessionNotes, patientId, patientName, manualEntries = []) {
  const entries = [];
  const notes = filterNotesForPatient(sessionNotes, patientId, patientName).filter(
    (n) => n.note_type === 'progress_note'
  );

  notes.forEach((note) => {
    const soap = getNoteContent(note).soap || {};
    const time = formatFlowSheetDateTime(note.datetime);
    const sections = [
      { text: soap.objective, preferredType: 'observation' },
      { text: soap.plan, preferredType: 'physician' },
    ];

    sections.forEach(({ text, preferredType }) => {
      splitIntoEntries(text).forEach((line, index) => {
        entries.push({
          id: `${note.id}-${preferredType}-${index}`,
          time,
          sortAt: note.datetime,
          type: classifyObservationText(line) === 'observation' ? preferredType : classifyObservationText(line),
          content: line,
          source: 'auto',
        });
      });
    });
  });

  manualEntries.forEach((entry) => {
    const resolved = resolveEntryDateTime(entry, entry.datetime || null);
    entries.push({
      id: entry.id,
      time: formatFlowSheetDateTime(resolved) !== '—'
        ? formatFlowSheetDateTime(resolved)
        : entry.time || '—',
      sortAt: resolved ? resolved.toISOString() : composeDateTimeFromClockTime(entry.time),
      type: entry.type,
      content: entry.content,
      source: 'manual',
    });
  });

  return entries.sort(sortByResolvedDateTime);
}

export function calculateFluidBalance(inputs = [], outputs = []) {
  const totalInput = inputs.reduce(
    (sum, row) => sum + (Number(row.volumeInfused ?? row.volume_infused_ml) || 0),
    0
  );
  const totalOutput = outputs.reduce(
    (sum, row) => sum + (Number(row.volume ?? row.volume_ml) || 0),
    0
  );
  const net = totalInput - totalOutput;
  return { totalInput, totalOutput, net };
}

function enrichFluidRow(row, referenceIso = null) {
  const resolved = resolveEntryDateTime(row, referenceIso || row.datetime || null);
  const datetime = resolved ? resolved.toISOString() : row.datetime || null;
  return {
    ...row,
    datetime,
    sortAt: datetime,
    time: formatFlowSheetDateTime(datetime) !== '—'
      ? formatFlowSheetDateTime(datetime)
      : row.time || '—',
    dayKey: datetime ? getCalendarDayKey(datetime) : null,
  };
}

function fluidInputFromNote(note, entry, index) {
  return enrichFluidRow(
    {
      id: `${note.id}-in-${index}`,
      time: entry.time_given || formatTime(note.datetime),
      datetime: entry.datetime || note.datetime,
      type: entry.type || 'Other',
      typeOther: entry.type_other || '',
      volumeOrdered: entry.volume_ordered_ml ?? '',
      volumeInfused: entry.volume_infused_ml ?? '',
      rate: entry.rate_ml_hr ?? '',
      running: entry.running !== false,
      source: 'voice',
    },
    note.datetime
  );
}

function fluidOutputFromNote(note, entry, index) {
  return enrichFluidRow(
    {
      id: `${note.id}-out-${index}`,
      time: entry.time_given || formatTime(note.datetime),
      datetime: entry.datetime || note.datetime,
      type: entry.type || 'Other',
      typeOther: entry.type_other || '',
      volume: entry.volume_ml ?? '',
      source: 'voice',
    },
    note.datetime
  );
}

export function buildMergedFluidData(sessionNotes, patientId, patientName, flowSheetData = {}) {
  const manualInputs = (flowSheetData.fluidInputs || []).map((row) =>
    enrichFluidRow({ ...row, source: row.source || 'manual' })
  );
  const manualOutputs = (flowSheetData.fluidOutputs || []).map((row) =>
    enrichFluidRow({ ...row, source: row.source || 'manual' })
  );

  const voiceInputs = [];
  const voiceOutputs = [];

  filterNotesForPatient(sessionNotes, patientId, patientName)
    .filter((n) => n.note_type === 'fluid_balance')
    .forEach((note) => {
      const content = getNoteContent(note);
      (content.fluid_inputs || []).forEach((entry, index) => {
        voiceInputs.push(fluidInputFromNote(note, entry, index));
      });
      (content.fluid_outputs || []).forEach((entry, index) => {
        voiceOutputs.push(fluidOutputFromNote(note, entry, index));
      });
    });

  const fluidInputs = [...voiceInputs, ...manualInputs].sort(sortByResolvedDateTime);
  const fluidOutputs = [...voiceOutputs, ...manualOutputs].sort(sortByResolvedDateTime);

  return { fluidInputs, fluidOutputs };
}

/** Filter fluid rows to one calendar day (resets at local midnight). */
export function filterFluidRowsForCalendarDay(rows = [], day = new Date()) {
  const dayKey = getCalendarDayKey(day);
  return rows.filter((row) => {
    const key = row.dayKey || getCalendarDayKey(resolveEntryDateTime(row));
    return key === dayKey;
  });
}

/**
 * 24-hour fluid balance for a calendar day (resets at 00:00, not shift start).
 */
export function calculateFluidBalanceForCalendarDay(inputs = [], outputs = [], day = new Date()) {
  const dayInputs = filterFluidRowsForCalendarDay(inputs, day);
  const dayOutputs = filterFluidRowsForCalendarDay(outputs, day);
  const balance = calculateFluidBalance(dayInputs, dayOutputs);
  const dayDate = day instanceof Date ? day : new Date(day);
  return {
    ...balance,
    dayKey: getCalendarDayKey(dayDate),
    dayLabel: formatClinicalCalendarDay(dayDate, 'en'),
    entryCount: dayInputs.length + dayOutputs.length,
  };
}

export { dash, formatTime, VITAL_COLUMNS };
